import { useState } from 'react';
import { Badge, CloseButton, Group, Select, Stack, Text, Title } from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useLeasesList } from '../queries/leases';
import { useLeaseTracts } from '../queries/lease-tracts';
import {
  useAddSharedAcreage,
  useRemoveSharedAcreage,
  useSharedAcreage,
} from '../queries/lease-tract-shared-acreage';
import { ApiError } from './api-error';

interface Props {
  /** The saved tract's id, or null for a brand-new/unsaved tract. */
  tractId: string | null;
}

/**
 * "Shared acreage" section for TractFormDrawer — the self-referential
 * counterpart of CrossReferenceSection.tsx (spec docs/superpowers/specs/
 * 2026-08-16-land-data-lease-tracts-design.md §3.2/§4.2). Unlike
 * CrossReferenceSection there's only ONE related type (another
 * lease_tracts row), so there's a single "Linked tracts" group rather than
 * one group per related type.
 *
 * When `tractId` is null (a brand-new, unsaved tract) there is nothing to
 * link yet — the section renders a muted note instead of the live
 * controls, same as CrossReferenceSection.
 */
export function SharedAcreageSection({ tractId }: Props) {
  const [pickerValue, setPickerValue] = useState<string | null>(null);

  const sharedQ = useSharedAcreage(tractId);
  const addM = useAddSharedAcreage(tractId);
  const removeM = useRemoveSharedAcreage(tractId);

  // The project's full tract + lease lists — same lists the lease-tracts
  // section / lease list page use, so this shares the query cache rather
  // than issuing a redundant fetch. Labels are built client-side to match
  // the API's labelSelectSql (api/src/routes/lease-tract-shared-acreage.ts:
  // "<agreement_number> · <lease_number>").
  const tractsQ = useLeaseTracts();
  const leasesQ = useLeasesList();
  const leaseNumberById = new Map((leasesQ.data ?? []).map((l) => [l.id, l.lease_number]));

  if (tractId == null) {
    return (
      <Stack gap="xs">
        <Title order={5}>Shared acreage</Title>
        <Text size="sm" c="dimmed">
          Save the tract first to link shared acreage.
        </Text>
      </Stack>
    );
  }

  const linked = sharedQ.data ?? [];
  const linkedIds = new Set(linked.map((t) => t.id));
  const candidates = (tractsQ.data ?? [])
    .filter((t) => t.id !== tractId && !linkedIds.has(t.id))
    .map((t) => ({
      value: t.id,
      label: `${t.agreement_number} · ${leaseNumberById.get(t.lease_id) ?? ''}`,
    }));

  async function handleAdd(targetTractId: string) {
    try {
      await addM.mutateAsync({ target_tract_id: targetTractId });
    } catch (err) {
      notifications.show({
        title: 'Link failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  async function handleRemove(targetTractId: string) {
    try {
      await removeM.mutateAsync(targetTractId);
    } catch (err) {
      notifications.show({
        title: 'Remove failed',
        message: err instanceof ApiError ? err.message : 'Unexpected error',
        color: 'red',
      });
    }
  }

  return (
    <Stack gap="md">
      <Title order={5}>Shared acreage</Title>

      <Stack gap={4}>
        <Text size="sm" fw={500}>
          Linked tracts
        </Text>
        <Group gap="xs">
          {linked.length === 0 && (
            <Text size="xs" c="dimmed">
              None linked
            </Text>
          )}
          {linked.map((t) => (
            <Badge
              key={t.id}
              variant="light"
              color="indigo"
              rightSection={
                <CloseButton
                  size="xs"
                  variant="transparent"
                  aria-label={`Remove ${t.label}`}
                  onClick={() => void handleRemove(t.id)}
                />
              }
            >
              {t.remark ? `${t.label} (${t.remark})` : t.label}
            </Badge>
          ))}
        </Group>

        <Select
          label="+ Link a tract"
          placeholder="Search…"
          data={candidates}
          value={pickerValue}
          searchable
          clearable
          nothingFoundMessage="No candidates"
          onChange={(v) => {
            setPickerValue(null);
            if (v) void handleAdd(v);
          }}
        />
      </Stack>
    </Stack>
  );
}
